import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { ChevronDown } from 'lucide-react'
import { useUnit, useCreateUnit, useUpdateUnit, useBuildingsForUnits } from '@/hooks/useUnits'
import type { UnitType } from '@/types/units'
import { UNIT_TYPE_LABELS, UNIT_TYPES, UNIT_STATUS_LABELS, UNIT_STATUSES, CAPACITY_STRATEGY_LABELS, CAPACITY_STRATEGIES, DOOR_TYPE_LABELS, DOOR_TYPES, RATE_TYPE_LABELS, RATE_TYPES, isAircraftUnit, isHangarType, isOutdoorUnitType, getDefaultCapacityStrategy, SURFACE_TYPES, SURFACE_TYPE_LABELS, LIGHTING_OPTIONS, LIGHTING_LABELS, POWER_OPTIONS, POWER_LABELS } from '@/types/units'

// Form validation schema
const unitFormSchema = z.object({
  // Basic Info
  building_id: z.string().min(1, 'Building is required'),
  unit_code: z.string().min(1, 'Unit code is required').max(20),
  name: z.string().nullable(),
  type: z.enum(['conventional_hangar', 'box_hangar', 't_hangar', 'tiedown', 'ramp', 'office', 'custom']).nullable(),
  capacity_strategy: z.enum(['single', 'stackable', 'area_based']).nullable(),
  status: z.enum(['available', 'leased', 'maintenance']),
  is_leasable: z.boolean(),
  // Interior Dimensions
  interior_width: z.number().nullable(),
  interior_depth: z.number().nullable(),
  interior_height: z.number().nullable(),
  usable_sqft: z.number().nullable(),
  // T-hangar dimensions
  door_width: z.number().nullable(),
  depth: z.number().nullable(),
  tail_width: z.number().nullable(),
  // Door Specifications
  door_height: z.number().nullable(),
  door_type: z.enum(['bifold', 'sliding', 'hydraulic', 'manual']).nullable(),
  // Aircraft Constraints
  max_wingspan: z.number().nullable(),
  max_length: z.number().nullable(),
  max_tail_height: z.number().nullable(),
  // Position
  offset_x: z.number(),
  offset_y: z.number(),
  unit_rotation: z.number().nullable(),
  // Features
  climate_controlled: z.boolean(),
  heated: z.boolean(),
  electrical_service: z.string().nullable(),
  // Pricing
  default_monthly_rate: z.number().nullable(),
  default_transient_rate: z.number().nullable(),
  rate_type: z.enum(['flat', 'per_sqft', 'per_night']).nullable(),
  pricing_override: z.boolean(),
  unit_monthly_rate: z.number().nullable(),
  unit_rate_per_sqft: z.number().nullable(),
  // Tiedown-specific config (stored in type_config JSON)
  tiedown_surface_type: z.enum(['concrete', 'asphalt', 'gravel', 'grass']).nullable(),
  tiedown_tie_down_points: z.number().nullable(),
  tiedown_covered: z.boolean(),
  tiedown_has_chocks: z.boolean(),
  tiedown_lighting: z.enum(['none', 'standard', 'led']).nullable(),
  tiedown_power_hookup: z.enum(['none', '15amp', '30amp', '50amp']).nullable(),
})

type UnitFormValues = z.infer<typeof unitFormSchema>

interface UnitsMutateDrawerProps {
  /** Whether the drawer is open */
  open: boolean
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void
  /** Unit ID for edit mode, undefined for create mode */
  unitId?: string
}

/**
 * Right side panel drawer for unit create/edit
 * Follows shadcn-admin drawer pattern with type-dependent form sections
 */
export function UnitsMutateDrawer({ open, onOpenChange, unitId }: UnitsMutateDrawerProps) {
  const isEdit = !!unitId

  // Collapsible section states
  const [featuresOpen, setFeaturesOpen] = useState(false)
  const [pricingOpen, setPricingOpen] = useState(false)

  // Exit confirmation
  const [showExitConfirm, setShowExitConfirm] = useState(false)
  const [pendingClose, setPendingClose] = useState(false)

  // Fetch existing unit for edit mode
  const { data: unit, isLoading: isLoadingUnit } = useUnit(isEdit ? unitId : undefined)

  // Fetch buildings for dropdown
  const { data: buildings, isLoading: isLoadingBuildings } = useBuildingsForUnits()

  // Mutations
  const createUnit = useCreateUnit()
  const updateUnit = useUpdateUnit()

  const isMutating = createUnit.isPending || updateUnit.isPending

  // Initialize form
  const form = useForm<UnitFormValues>({
    resolver: zodResolver(unitFormSchema),
    defaultValues: {
      building_id: '',
      unit_code: '',
      name: null,
      type: null,
      capacity_strategy: null,
      status: 'available',
      is_leasable: true,
      interior_width: null,
      interior_depth: null,
      interior_height: null,
      usable_sqft: null,
      door_width: null,
      depth: null,
      tail_width: null,
      door_height: null,
      door_type: null,
      max_wingspan: null,
      max_length: null,
      max_tail_height: null,
      offset_x: 0,
      offset_y: 0,
      unit_rotation: null,
      climate_controlled: false,
      heated: false,
      electrical_service: null,
      default_monthly_rate: null,
      default_transient_rate: null,
      rate_type: null,
      pricing_override: false,
      unit_monthly_rate: null,
      unit_rate_per_sqft: null,
      // Tiedown config defaults
      tiedown_surface_type: null,
      tiedown_tie_down_points: null,
      tiedown_covered: false,
      tiedown_has_chocks: true,
      tiedown_lighting: null,
      tiedown_power_hookup: null,
    },
  })

  const { isDirty } = form.formState
  const watchedType = form.watch('type')
  const watchedPricingOverride = form.watch('pricing_override')

  // Type-dependent visibility
  const showAircraftConstraints = watchedType ? isAircraftUnit(watchedType as UnitType) : false
  const showInteriorDimensions = watchedType ? isHangarType(watchedType as UnitType) : false
  const showDoorSpecs = watchedType ? !isOutdoorUnitType(watchedType as UnitType) : true
  const showTiedownConfig = watchedType === 'tiedown'

  // Update capacity strategy when type changes
  useEffect(() => {
    if (watchedType && !isEdit) {
      const defaultStrategy = getDefaultCapacityStrategy(watchedType as UnitType)
      form.setValue('capacity_strategy', defaultStrategy)
    }
  }, [watchedType, isEdit, form])

  // Populate form when editing
  useEffect(() => {
    if (unit && isEdit) {
      form.reset({
        building_id: unit.building_id,
        unit_code: unit.unit_code,
        name: unit.name,
        type: unit.type,
        capacity_strategy: unit.capacity_strategy,
        status: unit.status,
        is_leasable: unit.is_leasable ?? true,
        interior_width: unit.interior_width,
        interior_depth: unit.interior_depth,
        interior_height: unit.interior_height,
        usable_sqft: unit.usable_sqft,
        door_width: unit.door_width,
        depth: unit.depth,
        tail_width: unit.tail_width,
        door_height: unit.door_height,
        door_type: unit.door_type,
        max_wingspan: unit.max_wingspan,
        max_length: unit.max_length,
        max_tail_height: unit.max_tail_height,
        offset_x: unit.offset_x ?? 0,
        offset_y: unit.offset_y ?? 0,
        unit_rotation: unit.unit_rotation,
        climate_controlled: unit.climate_controlled ?? false,
        heated: unit.heated ?? false,
        electrical_service: unit.electrical_service,
        default_monthly_rate: unit.default_monthly_rate,
        default_transient_rate: unit.default_transient_rate,
        rate_type: unit.rate_type,
        pricing_override: unit.pricing_override ?? false,
        unit_monthly_rate: unit.unit_monthly_rate,
        unit_rate_per_sqft: unit.unit_rate_per_sqft,
      })
    }
  }, [unit, isEdit, form])

  // Reset form when opening for create
  useEffect(() => {
    if (open && !isEdit) {
      form.reset()
    }
  }, [open, isEdit, form])

  // Handle drawer close with dirty check
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && isDirty) {
        setPendingClose(true)
        setShowExitConfirm(true)
        return
      }
      onOpenChange(newOpen)
    },
    [isDirty, onOpenChange]
  )

  // Handle confirmed close
  const handleConfirmClose = useCallback(() => {
    setShowExitConfirm(false)
    if (pendingClose) {
      setPendingClose(false)
      form.reset()
      onOpenChange(false)
    }
  }, [pendingClose, onOpenChange, form])

  // Handle cancel close
  const handleCancelClose = useCallback(() => {
    setShowExitConfirm(false)
    setPendingClose(false)
  }, [])

  // Handle form submission
  const onSubmit = async (values: UnitFormValues) => {
    try {
      if (isEdit && unitId) {
        await updateUnit.mutateAsync({
          id: unitId,
          data: values as any,
        })
      } else {
        await createUnit.mutateAsync(values as any)
      }
      form.reset()
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving unit:', error)
    }
  }

  const title = isEdit ? `Edit Unit: ${unit?.unit_code || '...'}` : 'Create Unit'
  const description = isEdit
    ? 'Update unit details below.'
    : 'Add a new unit to the building.'

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="flex flex-col w-full sm:max-w-2xl p-0 overflow-hidden">
          <SheetHeader className="px-4 py-4 border-b shrink-0">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1 min-h-0">
            <Form {...form}>
              <form id="unit-form" onSubmit={form.handleSubmit(onSubmit)} className="px-4 py-4 space-y-6">
                {/* Building Selection (Create mode only) */}
                {!isEdit && (
                  <FormField
                    control={form.control}
                    name="building_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Building *</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={isLoadingBuildings || isMutating}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a building" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {buildings?.map((building) => (
                              <SelectItem key={building.id} value={building.id}>
                                {building.name} ({building.building_code})
                                {building.location_name && (
                                  <span className="text-muted-foreground ml-1">
                                    - {building.location_name}
                                  </span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Unit Code */}
                <FormField
                  control={form.control}
                  name="unit_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit Code *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="A-01"
                          className="font-mono uppercase"
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          disabled={isMutating}
                        />
                      </FormControl>
                      <FormDescription>
                        Unique identifier within the building
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Name (optional) */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value || null)}
                          placeholder="Optional display name"
                          disabled={isMutating}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Type */}
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit Type *</FormLabel>
                      <Select
                        value={field.value ?? ''}
                        onValueChange={(v) => field.onChange(v || null)}
                        disabled={isMutating}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {UNIT_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {UNIT_TYPE_LABELS[type]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Determines form sections and aircraft constraints
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Capacity Strategy */}
                <FormField
                  control={form.control}
                  name="capacity_strategy"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Capacity Strategy</FormLabel>
                      <Select
                        value={field.value ?? ''}
                        onValueChange={(v) => field.onChange(v || null)}
                        disabled={isMutating}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select strategy" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CAPACITY_STRATEGIES.map((strategy) => (
                            <SelectItem key={strategy} value={strategy}>
                              {CAPACITY_STRATEGY_LABELS[strategy]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Status & Leasable */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={isMutating}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {UNIT_STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {UNIT_STATUS_LABELS[s]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="is_leasable"
                    render={({ field }) => (
                      <FormItem className="flex flex-col justify-end">
                        <div className="flex items-center gap-2 h-10">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isMutating}
                            />
                          </FormControl>
                          <FormLabel className="!mt-0">Leasable</FormLabel>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Interior Dimensions (Hangar types only) */}
                {showInteriorDimensions && (
                  <>
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">Interior Dimensions (ft)</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="interior_width"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Width</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="interior_depth"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Depth</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="interior_height"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Height</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="usable_sqft"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Usable Sq Ft</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                                disabled={isMutating}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <Separator />
                  </>
                )}

                {/* Door Specifications (Aircraft units with doors - not outdoor) */}
                {showAircraftConstraints && showDoorSpecs && (
                  <>
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">Door Specifications</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="door_width"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Door Width (ft)</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  placeholder="42"
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="door_height"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Door Height (ft)</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  placeholder="16"
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="door_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Door Type</FormLabel>
                            <Select
                              value={field.value ?? ''}
                              onValueChange={(v) => field.onChange(v || null)}
                              disabled={isMutating}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {DOOR_TYPES.map((type) => (
                                  <SelectItem key={type} value={type}>
                                    {DOOR_TYPE_LABELS[type]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />

                    {/* Aircraft Constraints */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">Aircraft Constraints (ft)</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="max_wingspan"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Max Wingspan</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="max_length"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Max Length</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="max_tail_height"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Max Tail Height</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                    <Separator />
                  </>
                )}

                {/* Tiedown Configuration (Tiedown type only) */}
                {showTiedownConfig && (
                  <>
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">Tiedown Configuration</h3>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="tiedown_surface_type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Surface Type</FormLabel>
                              <Select
                                value={field.value ?? ''}
                                onValueChange={(v) => field.onChange(v || null)}
                                disabled={isMutating}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select surface" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {SURFACE_TYPES.map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {SURFACE_TYPE_LABELS[type]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="tiedown_tie_down_points"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tie-Down Points</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={0}
                                  max={6}
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                                  placeholder="3"
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="tiedown_lighting"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Lighting</FormLabel>
                              <Select
                                value={field.value ?? ''}
                                onValueChange={(v) => field.onChange(v || null)}
                                disabled={isMutating}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select lighting" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {LIGHTING_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {LIGHTING_LABELS[option]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="tiedown_power_hookup"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Power Hookup</FormLabel>
                              <Select
                                value={field.value ?? ''}
                                onValueChange={(v) => field.onChange(v || null)}
                                disabled={isMutating}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select power" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {POWER_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {POWER_LABELS[option]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="flex flex-col gap-4">
                        <FormField
                          control={form.control}
                          name="tiedown_covered"
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-between">
                              <FormLabel className="!mt-0">Covered (Shade Structure)</FormLabel>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isMutating}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="tiedown_has_chocks"
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-between">
                              <FormLabel className="!mt-0">Wheel Chocks Available</FormLabel>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isMutating}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                    <Separator />
                  </>
                )}

                {/* Features (Collapsible) */}
                <Collapsible open={featuresOpen} onOpenChange={setFeaturesOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                      <h3 className="text-sm font-medium">Features</h3>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${featuresOpen ? 'rotate-180' : ''}`}
                      />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4 pt-4">
                    <div className="flex flex-col gap-4">
                      <FormField
                        control={form.control}
                        name="climate_controlled"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between">
                            <FormLabel className="!mt-0">Climate Controlled</FormLabel>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isMutating}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="heated"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between">
                            <FormLabel className="!mt-0">Heated</FormLabel>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isMutating}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="electrical_service"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Electrical Service</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value || null)}
                                placeholder="e.g., 120V, 240V, GPU"
                                disabled={isMutating}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                {/* Pricing (Collapsible) */}
                <Collapsible open={pricingOpen} onOpenChange={setPricingOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                      <h3 className="text-sm font-medium">Pricing</h3>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${pricingOpen ? 'rotate-180' : ''}`}
                      />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="default_monthly_rate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Monthly Rate ($)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                disabled={isMutating}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="default_transient_rate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Transient Rate ($)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                disabled={isMutating}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="rate_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Rate Type</FormLabel>
                          <Select
                            value={field.value ?? ''}
                            onValueChange={(v) => field.onChange(v || null)}
                            disabled={isMutating}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select rate type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {RATE_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {RATE_TYPE_LABELS[type]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />

                    <FormField
                      control={form.control}
                      name="pricing_override"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between">
                          <div>
                            <FormLabel className="!mt-0">Custom Pricing Override</FormLabel>
                            <FormDescription>
                              Override building default pricing
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isMutating}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {watchedPricingOverride && (
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="unit_monthly_rate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Override Monthly ($)</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="unit_rate_per_sqft"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Override $/Sq Ft</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                                  disabled={isMutating}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </form>
            </Form>
          </ScrollArea>

          <SheetFooter className="flex-row justify-end gap-2 px-4 py-4 border-t bg-background shrink-0">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="unit-form"
              disabled={isMutating || isLoadingUnit}
            >
              {isMutating ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close? Your
              changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Continue editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
